import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { useDispatch, useSelector } from "react-redux";
import Toasts from "../../../core/common/toast";
import dayjs from "dayjs";
import { findAllEmployeesAttendance } from "../../../core/data/redux/reportingEmployeesSlice";
import MonthYearDropdown from "./MonthYearDropdown";

interface ReportingAttendenceProps {
  empID?: string;
}

const ReportingAttendence: React.FC<ReportingAttendenceProps> = ({ empID }) => {
  const location = useLocation();
  const stateEmpId = location.state?.empId;
  const empId = empID || stateEmpId;


  

  const reportingEmployeesState = useSelector(
    (state: any) => state.reportingEmployeesState
  );

  const [dataList, setDataList] = useState<any[]>([]);
  const [query, setQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const dispatch = useDispatch();

  function debounce(func: (...args: any[]) => void, delay: number) {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  const fetchRecords = (
    pagination = { current: 1, pageSize: 10 },
    empId: string = "",
    payload?: { month: string; year: string }
  ) => {
    dispatch(
      findAllEmployeesAttendance({
        page: pagination.current,
        limit: pagination.pageSize,
        empId: empId,
        ...(payload ? { month: payload.month, year: payload.year } : {}),
      })
    );
  };

  const debouncedSearch = useRef(
    debounce((value: string) => {
      const trimmed = value.trim();
      if (trimmed === "") {
        setQuery("");
        fetchRecords(pagination, empId);
        return;
      }
      if (trimmed.length < 4) return;
      setQuery(trimmed);
      fetchRecords(pagination, trimmed);
    }, 500)
  ).current;

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  useEffect(() => {
    if (empId) {
      fetchRecords(pagination, empId);
    }
  }, [empId]);

  useEffect(() => {
    if (
      reportingEmployeesState.loading === "fulfilled" &&
      reportingEmployeesState.action === "assign"
    ) {
      fetchRecords(pagination, empId);
    } else if (
      reportingEmployeesState.loading === "fulfilled" &&
      reportingEmployeesState.action === "list"
    ) {
      const result = reportingEmployeesState?.data?.data || null;
      if (result?.data && result.data.length > 0) {
        const employees = result.data;
        setDataList(employees);
        setPagination({
          ...pagination,
          total: result.total || employees.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    }
  }, [reportingEmployeesState.loading]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PRESENT: "#049123",
      ABSENT: "#C0282C",
      HALF_DAY: "#FFA500",
      LATE_COMING: "#B09BFB",
      EARLY_LEAVING: "#FFFF00",
      HOLIDAY: "#969696",
    };
    return colors[status] || "#ccc";
  };

  const attendanceColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 80,
      render: (text: string, record: any, index: number) =>
        index + 1 + (pagination.current - 1) * pagination.pageSize,
    },
    { 
      title: "Emp ID", 
      dataIndex: "empId",
      render: (empId: string) => parseInt(empId, 10),
    },
    {
      title: "Check In",
      dataIndex: "check_in_time",
      render: (text: string, record: any) =>
        text ? dayjs(record.check_in_time).format("DD-MM-YYYY hh:mm A") : "-",
    },
    {
      title: "Check Out",
      dataIndex: "check_out_time",
      render: (text: string, record: any) =>
        text
          ? dayjs(record.check_out_time).format("DD-MM-YYYY hh:mm A")
          : "-",
    },
    {
      title: "Working Hours",
      dataIndex: "working_hours",
      render: (text: string) => text || "-",
    },
    {
      title: "Wing",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.wing_name ? `${employee?.details?.sap_department_designation_wings?.wing_name}` : "",
    },
    {
      title: "Department",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.department_name ? `${employee?.details?.sap_department_designation_wings?.department_name}` : "",
    },
    {
      title: "Designation",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.designation_name ? `${employee?.details?.sap_department_designation_wings?.designation_name}` : "",
    },
    {
      title: "Attendance Status",
      dataIndex: "attendance_status",
      render: (text: string) => (
        <span
          style={{
            backgroundColor: getStatusColor(text),
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
          }}
        >
          {text?.replace(/_/g, " ")?.replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>
      ),
    },
    {
      title: "Verification",
      dataIndex: "is_verified",
    },
  ];

  const handlePagination = (pagination: any) => {
    setPagination({
      current: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total || 10,
    });
    fetchRecords(pagination, empId);
  };

  return (
    <div>
      <Toasts
        show={reportingEmployeesState.loading === "rejected"}
        message={reportingEmployeesState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={
          reportingEmployeesState.loading === "fulfilled" &&
          reportingEmployeesState.action !== "list"
        }
        message={reportingEmployeesState?.data?.message}
        title={"Success"}
        type={"success"}
      />
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Attendence</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Employees</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">My Team</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Attendence
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3">Attendence</h4>
              {/* ✅ Updated: Month and Year Dropdown */}
              <MonthYearDropdown
                onChange={({ month, year }) =>
                  fetchRecords(pagination, empId, { month, year })
                }
              />
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={attendanceColumns}
                dataSource={dataList}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={reportingEmployeesState.loading === "pending"}
                handleSearch={handleSearch}
              />
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-md-12">
              <div className="datatable-paginate mt-4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportingAttendence;
